import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import OnboardingRoute from "@/components/OnboardingRoute/OnboardingRoute";
import ProtectedRoute from "@/components/ProtectedRoute/ProtectedRoute";
import PublicRoute from "@/components/PublicRoute/PublicRoute";
import RequireOrganisationRoute from "@/components/RequireOrganisationRoute/RequireOrganisationRoute";
import VolunteerProtectedRoute from "@/components/VolunteerProtectedRoute/VolunteerProtectedRoute";
import VolunteerPublicRoute from "@/components/VolunteerPublicRoute/VolunteerPublicRoute";

import { getPagePath, PAGES } from "../../config/pages";
import type { AppLanguage } from "../../i18n/config";
import {
  buildLocalizedPath,
  detectPreferredLanguage,
  isSupportedLanguage,
} from "../../i18n/routing";
import useSyncLanguage from "../../i18n/useSyncLanguage";
import AppShell from "../AppShell";
import {
  BillingReturn,
  Contracts,
  ContractTemplates,
  CookiePolicy,
  EventDetail,
  EventsList,
  FinanceSettings,
  ForgotPassword,
  GeneralSettings,
  Home,
  InstaAvatarExport,
  InstaCarouselExport,
  InstaPostExport,
  InstaStoryExport,
  InvitationsSettings,
  Login,
  MembersSettings,
  NewEvent,
  NewVolunteer,
  NotFound,
  Onboarding,
  OrganisationInvitation,
  PlanSettings,
  Pricing,
  Profile,
  PublicGuestList,
  ReferentialsSettings,
  Register,
  ResetPassword,
  SettingsLayout,
  TermsOfUses,
  VolunteerDashboard,
  VolunteerEventGuests,
  VolunteerForgotPassword,
  VolunteerLinkSettings,
  VolunteerLogin,
  VolunteerResetPassword,
  VolunteerSignup,
  VolunteersList,
} from "./lazyPages";

/**
 * Sends a URL that carries no language to the same page in the visitor's own.
 *
 * The path and query string are KEPT: anything linking in from outside — an
 * email, a bookmark, Stripe's return URL — has no way to know the language
 * prefix, and dropping the destination sent those visitors to the home page
 * instead. A customer coming back from a payment landing on the marketing page
 * has no idea whether the payment went through.
 */
function LangRedirect() {
  const { pathname, search, hash } = useLocation();

  return (
    <Navigate
      to={`${buildLocalizedPath(detectPreferredLanguage(), pathname)}${search}${hash}`}
      replace
    />
  );
}

function LocalizedApp() {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  const currentLanguage: AppLanguage | null = isSupportedLanguage(lang)
    ? lang
    : null;

  useSyncLanguage(currentLanguage);

  useEffect(() => {
    if (!currentLanguage) {
      /**
       * `/:lang/*` also matches a URL that carries no language at all:
       * `/billing/success` arrives here as lang="billing". Sending those to
       * the home page threw the destination away — which is what put a
       * customer returning from a payment on the marketing page, with no idea
       * whether their payment went through.
       *
       * `buildLocalizedPath` only strips prefixes that are real languages, so
       * "billing" survives as part of the path.
       */
      navigate(
        `${buildLocalizedPath(detectPreferredLanguage(), pathname)}${search}${hash}`,
        { replace: true },
      );
    }
  }, [currentLanguage, navigate, pathname, search, hash]);

  if (!currentLanguage) {
    return null;
  }

  return (
    <Routes>
      {/* Public marketing pages — standalone, rendered without the AppShell */}
      <Route index element={<Home />} />
      <Route path={PAGES.pricing} element={<Pricing />} />

      {/* Stripe return pages — reached by redirect from Stripe's domain, so
          they stay outside every guard: a customer who paid must land on an
          explanation, never on a login wall. */}
      <Route
        path={PAGES.billingSuccess}
        element={<BillingReturn outcome="success" />}
      />
      <Route
        path={PAGES.billingCancelled}
        element={<BillingReturn outcome="cancelled" />}
      />

      {/* Auth pages — no navbar */}
      <Route element={<PublicRoute />}>
        <Route path={PAGES.login} element={<Login />} />
        <Route path={PAGES.register} element={<Register />} />
        <Route path={PAGES.forgotPassword} element={<ForgotPassword />} />
        <Route path={PAGES.resetPassword} element={<ResetPassword />} />
      </Route>
      <Route
        path={PAGES.organisationInvitation}
        element={<OrganisationInvitation />}
      />
      <Route element={<VolunteerPublicRoute />}>
        <Route path={PAGES.volunteerLogin} element={<VolunteerLogin />} />
        <Route
          path={PAGES.volunteerForgotPassword}
          element={<VolunteerForgotPassword />}
        />
        <Route
          path={PAGES.volunteerResetPassword}
          element={<VolunteerResetPassword />}
        />
      </Route>
      <Route path={PAGES.volunteerSignup} element={<VolunteerSignup />} />
      <Route path={PAGES.publicGuestList} element={<PublicGuestList />} />
      <Route path={PAGES.cookiePolicy} element={<CookiePolicy />} />
      <Route path={PAGES.termsOfUse} element={<TermsOfUses />} />
      <Route element={<VolunteerProtectedRoute />}>
        <Route
          path={PAGES.volunteerDashboard}
          element={<VolunteerDashboard />}
        />
        <Route
          path={PAGES.volunteerEventGuests}
          element={<VolunteerEventGuests />}
        />
      </Route>

      {/* Onboarding — protected, no navbar, blocked if org already exists */}
      <Route element={<ProtectedRoute />}>
        <Route element={<OnboardingRoute />}>
          <Route path={PAGES.onboarding} element={<Onboarding />} />
        </Route>
      </Route>

      {/* App pages — full AppShell with navbar */}
      <Route
        element={
          <AppShell showLangSwitcher>
            <Outlet />
          </AppShell>
        }
      >
        <Route element={<ProtectedRoute />}>
          <Route element={<RequireOrganisationRoute />}>
            <Route path={PAGES.events} element={<EventsList />} />
            <Route path={PAGES.newEvent} element={<NewEvent />} />
            <Route path={PAGES.eventDetail} element={<EventDetail />} />
            <Route path={PAGES.contracts} element={<Contracts />} />
            <Route path={PAGES.profile} element={<Profile />} />
            <Route
              path={PAGES.settings}
              element={
                <Navigate
                  to={getPagePath("organisationSettings", currentLanguage)}
                  replace
                />
              }
            />
            <Route element={<SettingsLayout />}>
              <Route
                path={PAGES.organisationSettings}
                element={<GeneralSettings />}
              />
              <Route
                path={`${PAGES.organisationSettings}/:slug`}
                element={<GeneralSettings />}
              />
              <Route
                path={PAGES.settingsMembers}
                element={<MembersSettings />}
              />
              <Route
                path={PAGES.settingsInvitations}
                element={<InvitationsSettings />}
              />
              <Route
                path={PAGES.settingsVolunteerLink}
                element={<VolunteerLinkSettings />}
              />
              <Route
                path={PAGES.settingsReferentials}
                element={<ReferentialsSettings />}
              />
              <Route
                path={PAGES.settingsFinance}
                element={<FinanceSettings />}
              />
              <Route path={PAGES.settingsPlan} element={<PlanSettings />} />
              <Route
                path={PAGES.settingsContractTemplates}
                element={<ContractTemplates />}
              />
            </Route>
            <Route path={PAGES.volunteers} element={<VolunteersList />} />
            <Route path={PAGES.newVolunteer} element={<NewVolunteer />} />
          </Route>
        </Route>
      </Route>

      {/* 404 — standalone page, rendered without the AppShell */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function I18nRouter() {
  return (
    <Routes>
      <Route path="/:lang/*" element={<LocalizedApp />} />
      <Route
        path={PAGES.organisationInvitation}
        element={<OrganisationInvitation />}
      />
      {/* Emailed password-reset links carry no language prefix. Static routes
          outrank "/:lang/*", so they render directly instead of being treated
          as an unknown language and bounced to the home page. */}
      <Route path={PAGES.resetPassword} element={<ResetPassword />} />
      <Route
        path={PAGES.volunteerResetPassword}
        element={<VolunteerResetPassword />}
      />
      {/* Dev-only marketing capture routes — bare pages screenshotted by the
          frontend/scripts/ Puppeteer scripts, excluded from prod builds. */}
      {import.meta.env.DEV && (
        <>
          <Route path={PAGES.instaPostExport} element={<InstaPostExport />} />
          <Route path={PAGES.instaStoryExport} element={<InstaStoryExport />} />
          <Route
            path={PAGES.instaCarouselExport}
            element={<InstaCarouselExport />}
          />
          <Route
            path={PAGES.instaAvatarExport}
            element={<InstaAvatarExport />}
          />
        </>
      )}
      <Route path={PAGES.main} element={<LangRedirect />} />
      <Route path="*" element={<LangRedirect />} />
    </Routes>
  );
}

export default I18nRouter;
